import React, { useMemo } from 'react';
import { type Timestamp } from '@firebase/firestore';
import NumbersIcon from '@mui/icons-material/Numbers';
import { Alert, Stack, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import type { StatusActivityStatus } from '@shared/patient-intake-status';

import { useGetPatientData } from '../../../../../hooks/index.js';
import { SkeletonWrapper } from '../../../../../components/layout/index.js';
import { DetailsCard } from './DetailsCard.tsx';

const throwNever = (type: never) => {
  throw new Error(`Unknown verification status: ${type}`);
};

const verificationStatusToFriendlyText = (verificationStatus: StatusActivityStatus) => {
  switch (verificationStatus) {
    case 'COMPLETED':
      return 'Complete';
    case 'PENDING':
      return 'Pending';
    case 'NOT_STARTED':
      return 'Not Started';
    case 'ERROR':
      return 'Error';
    default:
      return throwNever(verificationStatus);
  }
};

type Medicare = {
  firstName: string;
  lastName: string;
  number: string;
  irn: string;
  expiry: string;
};

type IHI = {
  ihiNumber: string;
  firstName: string;
  lastName: string;
};

type VerifierSectionProps = {
  verifiedByName: string | null;
  verifiedAt: Timestamp | null;
  verificationStatus: StatusActivityStatus;
};

const VerifierSection: React.FC<VerifierSectionProps> = ({ verifiedByName, verifiedAt, verificationStatus }) => {
  if (!verifiedByName && !verifiedAt) {
    return null;
  }

  const byText = verificationStatus === 'COMPLETED' ? 'Verified by' : 'Updated by';

  return (
    <Stack direction="column" justifyContent="center" alignItems="center" gap={1} pb={2}>
      {verifiedByName && (
        <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
          <HealthAndSafetyIcon color="success" fontSize="small" />
          <Typography variant="body1">
            {byText} {verifiedByName}
          </Typography>
        </Stack>
      )}

      {verifiedAt && (
        <Typography variant="body2">
          on{' '}
          {verifiedAt.toDate().toLocaleString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
          })}
        </Typography>
      )}
    </Stack>
  );
};

type MedicareCardDetailsProps = {
  verifiedByName: string | null;
  verifiedAt: Timestamp | null;
  verificationStatus: StatusActivityStatus;
  patientId: string;
  hideTitle?: boolean;
};

export const MedicareIHIDetails: React.FC<MedicareCardDetailsProps> = ({
  verifiedByName,
  verifiedAt,
  verificationStatus,
  patientId,
  hideTitle = false,
}) => {
  const { data, isLoading, error } = useGetPatientData({ patientId });

  const medicareCard = useMemo(
    () => (data?.concessionCards?.['medicare card'] as Medicare) ?? null,
    [data?.concessionCards?.['medicare card']],
  );

  const ihi = useMemo(() => (data?.concessionCards?.['ihi'] as IHI) ?? null, [data?.concessionCards?.['ihi']]);

  const verificationStatusTitleText = verificationStatusToFriendlyText(verificationStatus);

  if (error) {
    return (
      <Stack gap={1} textAlign="center">
        <Typography variant="h6">Medicare / IHI Verification {verificationStatusTitleText}</Typography>
        <Alert severity="error">Error loading Medicare / IHI details</Alert>
      </Stack>
    );
  }

  return (
    <Stack gap={1} textAlign="center">
      {!hideTitle && <Typography variant="h6">Medicare / IHI Verification {verificationStatusTitleText}</Typography>}

      <VerifierSection
        verifiedByName={verifiedByName}
        verifiedAt={verifiedAt}
        verificationStatus={verificationStatus}
      />

      <Stack spacing={2} className="ph-no-capture">
        <DetailsCard title="Medicare Card" icon={<CreditCardIcon />}>
          <DetailsCard.Content>
            <DetailsCard.Field>
              <DetailsCard.Label>Name</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value
                  textToCopy={`${medicareCard?.firstName} ${medicareCard?.lastName}`}
                  disabled={!medicareCard}
                />
              </SkeletonWrapper>
            </DetailsCard.Field>
            <DetailsCard.Field>
              <DetailsCard.Label>Number</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={medicareCard?.number ?? ''} disabled={!medicareCard?.number} />
              </SkeletonWrapper>
            </DetailsCard.Field>
            <DetailsCard.Field>
              <DetailsCard.Label>IRN</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={medicareCard?.irn ?? ''} disabled={!medicareCard?.irn} />
              </SkeletonWrapper>
            </DetailsCard.Field>
            <DetailsCard.Field>
              <DetailsCard.Label>Expiry</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={medicareCard?.expiry ?? ''} disabled={!medicareCard?.expiry} />
              </SkeletonWrapper>
            </DetailsCard.Field>
          </DetailsCard.Content>
        </DetailsCard>

        <DetailsCard title="IHI Number" icon={<NumbersIcon />}>
          <DetailsCard.Content>
            <DetailsCard.Field>
              <DetailsCard.Label>IHI</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={ihi?.ihiNumber ?? ''} disabled={!ihi?.ihiNumber} />
              </SkeletonWrapper>
            </DetailsCard.Field>
            <DetailsCard.Field>
              <DetailsCard.Label>First Name</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={ihi?.firstName ?? ''} disabled={!ihi?.firstName} />
              </SkeletonWrapper>
            </DetailsCard.Field>
            <DetailsCard.Field>
              <DetailsCard.Label>Last Name</DetailsCard.Label>
              <SkeletonWrapper isLoading={isLoading}>
                <DetailsCard.Value textToCopy={ihi?.lastName ?? ''} disabled={!ihi?.lastName} />
              </SkeletonWrapper>
            </DetailsCard.Field>
          </DetailsCard.Content>
        </DetailsCard>
      </Stack>
    </Stack>
  );
};
